import React, { forwardRef, useCallback, useEffect, type ReactNode } from 'react';
import { ModalBody } from '@atlaskit/modal-dialog';
import { ClipboardContextBoundaryContainer as ClipboardBoundary } from '@atlassian/jira-clipboard/src/controllers/boundary/index.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JiraIssueCreateErrorBoundary } from '@atlassian/jira-issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import { useInlineFields } from '@atlassian/jira-forge-controllers-inline-fields-state/src/index.tsx';
import { getErrorType } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-error-type/index.tsx';
import { ISSUE_CREATE_MODAL_BODY_SELECTOR } from '@atlassian/jira-issue-create-commons/src/common/utils/select/index.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { useMinimizableModal } from '@atlassian/jira-minimizable-modal/src/controllers/use-minimizable-modal/index.tsx';
import type { MinimizableModalView } from '@atlassian/jira-minimizable-modal/src/types.tsx';
import { MinimizableModal } from '@atlassian/jira-minimizable-modal/src/ui/main.tsx';
import { FullscreenTransition } from '@atlassian/jira-minimizable-modal/src/ui/modal-container/fullscreen-transition/index.tsx';
import { MiniTransition } from '@atlassian/jira-minimizable-modal/src/ui/modal-container/mini-transition/index.tsx';
import { MinimizedTransition } from '@atlassian/jira-minimizable-modal/src/ui/modal-container/minimized-transition/index.tsx';
import { ModalTransition } from '@atlassian/jira-minimizable-modal/src/ui/modal-container/modal-transition/index.tsx';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import Footer from '../../../common/ui/footer/index.tsx';
import { useGICPositioningEnabledController } from '../../../controllers/gic-positioning-enabled/gic-positioning-enabled-controller/index.tsx';
import { useIsGICPositioningEnabled } from '../../../controllers/gic-positioning-enabled/main.tsx';
import {
	MinimizableModalWrapperPropsProvider,
	useMinimizableModalWrapperProps,
} from '../../../controllers/minimizable-modal-wrapper-props/main.tsx';
import type { MinimizableModalWrapperProps } from '../../../controllers/minimizable-modal-wrapper-props/types.tsx';
import { MinimizableFullscreen } from './minimizable-fullscreen/index.tsx';
import { MinimizableModalMini } from './minimizable-modal-mini/index.tsx';
import { MinimizableModalMinimized } from './minimizable-modal-minimized/index.tsx';
import Header from './minimizable-modal-modal/header/index.tsx';
import { MinimizableModalModal } from './minimizable-modal-modal/main.tsx';
import { useSetSummary } from './use-set-summary/index.tsx';
import { useViewChangerDisabler } from './use-view-changer-disabler/index.tsx';

const ModalBodyNext = forwardRef((props, ref) => (
	<ModalBody>
		{/* @ts-expect-error - TS2322 - Type '((instance: unknown) => void) | MutableRefObject<unknown> | null' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'. */}
		<div id={ISSUE_CREATE_MODAL_BODY_SELECTOR} ref={ref} {...props} />
	</ModalBody>
));

const OldModal = ({
	children,
	onModalCloseWithConfirm,
	shouldDisableActions,
	onSubmit,
	isSubmitting,
	isOperationInProgress,
	canCreateAnotherIssue,
	onCreateAnotherChange,
	getShouldDisableSubmit,
	createdIssues,
	displayCreateAnotherIssueToggle,
	showIssuesCreated,
	watchersConfigurations,
	dismissModalEntryPointWrapper,
	returnFocusRef,
}: MinimizableModalWrapperProps) => (
	<ShortcutScope>
		<ModalDialog
			width="large"
			height="100vh"
			autoFocus={false}
			// @ts-expect-error - TS2322 - Type '(_: SyntheticEvent<Element, Event>, analyticsEvent: UIAnalyticsEventInterface) => void' is not assignable to type 'OnCloseHandler'.
			onClose={onModalCloseWithConfirm('issueCreateModalEscapeClick')}
			shouldCloseOnEscapePress
			shouldCloseOnOverlayClick={false}
			testId="issue-create.ui.modal.modal-wrapper.modal"
			messageId="issue-create.ui.modal.modal-wrapper.modal-dialog"
			messageType="transactional"
			shouldReturnFocus={fg('alx-10155-focus-issues') ? returnFocusRef : undefined}
		>
			<Header
				shouldDisableActions={shouldDisableActions}
				watchersConfigurations={watchersConfigurations}
			/>
			{/* @ts-expect-error - TS2559 - Type '{ children: ReactNode; }' has no properties in common with type 'IntrinsicAttributes & RefAttributes<unknown>'. */}
			<ModalBodyNext>{children}</ModalBodyNext>
			<Footer
				onClose={onModalCloseWithConfirm('cancel')}
				onSubmit={onSubmit}
				isSubmitting={isSubmitting}
				isOperationInProgress={isOperationInProgress}
				canCreateAnotherIssue={canCreateAnotherIssue}
				onCreateAnotherChange={onCreateAnotherChange}
				shouldDisableSubmit={getShouldDisableSubmit()}
				createdIssues={createdIssues}
				displayCreateAnotherIssueToggle={displayCreateAnotherIssueToggle}
				showIssuesCreated={showIssuesCreated}
				dismissModalEntryPointWrapper={dismissModalEntryPointWrapper}
			/>
		</ModalDialog>
	</ShortcutScope>
);

const MinimizableModalContainer = ({ children }: { children?: ReactNode }) => {
	const { initialSummaryValue, returnFocusRef } = useMinimizableModalWrapperProps();

	const { view } = useMinimizableModal();

	const summaryValue = useSetSummary(initialSummaryValue);

	useViewChangerDisabler();

	return (
		<>
			<ModalTransition renderCondition={view === 'modal'}>
				<MinimizableModalModal
					data-testid="issue-create.ui.modal.modal-wrapper.modal"
					returnFocusRef={returnFocusRef}
				>
					{children}
				</MinimizableModalModal>
			</ModalTransition>
			<MiniTransition renderCondition={view === 'mini'}>
				{({ transitionProps }) => (
					<MinimizableModalMini {...transitionProps} summaryValue={summaryValue}>
						{children}
					</MinimizableModalMini>
				)}
			</MiniTransition>
			<FullscreenTransition renderCondition={view === 'fullscreen'}>
				{({ transitionProps }) => (
					<MinimizableFullscreen {...transitionProps}>{children}</MinimizableFullscreen>
				)}
			</FullscreenTransition>
			<MinimizedTransition renderCondition={view === 'minimized'}>
				{({ transitionProps }) => (
					<MinimizableModalMinimized {...transitionProps} summaryValue={summaryValue} />
				)}
			</MinimizedTransition>
		</>
	);
};

const OldModalWrapper = (props: MinimizableModalWrapperProps) => {
	const [{ disableGICPositioning }, { setDisableGICPositioning }] =
		useGICPositioningEnabledController();
	useEffect(() => {
		if (!disableGICPositioning) {
			setDisableGICPositioning(true);
		}
	}, [disableGICPositioning, setDisableGICPositioning]);
	return <OldModal {...props} />;
};

export const ModalWrapper = ({ children, ...rest }: MinimizableModalWrapperProps) => {
	const [{ minimizableModalView, displayMinimizableButtons }, { setMinimizableModalView }] =
		useTriggerIssueCreateModal();
	const isGICPositioningEnabled = useIsGICPositioningEnabled();
	const [, { dispatchFormSubmits: dispatchForgeInlineFormSubmits }] = useInlineFields();
	const [{ isFormLoaded }] = useFormContext();

	const renderOldModal = () => <OldModalWrapper {...rest}>{children}</OldModalWrapper>;

	const onViewChange = useCallback(
		(newView: MinimizableModalView) => {
			setMinimizableModalView(newView);
			dispatchForgeInlineFormSubmits();
		},
		[setMinimizableModalView, dispatchForgeInlineFormSubmits],
	);

	if (isGICPositioningEnabled()) {
		return (
			<UFOSegment name="issue-create-modal">
				<JiraIssueCreateErrorBoundary
					id={isFormLoaded ? 'issue-create-app-loaded' : 'issue-create-app'}
					packageName="jiraIssueCreateModal"
					fallback={renderOldModal}
					sendToPrivacyUnsafeSplunk
					attributes={(error: Error) => ({
						errorType: getErrorType(error),
					})}
					taskName="viewGlobalIssueCreateModal"
				>
					<MinimizableModalWrapperPropsProvider {...rest}>
						<MinimizableModal
							view={minimizableModalView}
							onViewChange={onViewChange}
							Container={MinimizableModalContainer}
							// @ts-expect-error - TS2322 - Type '(_: SyntheticEvent<Element, Event>, analyticsEvent: UIAnalyticsEventInterface) => void' is not assignable to type 'OnCloseHandler'.
							onClose={rest.onModalCloseWithConfirm('cancel')}
							displayMinimizableButtons={displayMinimizableButtons}
						>
							<ClipboardBoundary>{children}</ClipboardBoundary>
						</MinimizableModal>
					</MinimizableModalWrapperPropsProvider>
				</JiraIssueCreateErrorBoundary>
			</UFOSegment>
		);
	}
	return renderOldModal();
};
